import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { LeaveRequestService } from '../../../services/leave-request.service';
import { LeaveEncashService } from '../../../services/leave-encashment.service';
import { AttendanceRegularizationService } from '../../../services/attendance-regularization.service';
import { DocumentRequestService } from '../../../services/document-request.service';
import { TravelRequestService } from '../../../services/travel-request.service';
import { ExpenseRequestService } from '../../../services/expense-request.service';
import { LoanRequestService } from '../../../services/loan-request.service';
import { LoanAdjustmentService, buildApprovalPayload } from '../../../services/loan-adjustment.service';
import { LeaveResumptionService } from '../../../services/leave-resumption.service';

export type InboxDecision = 'approve' | 'reject';

type DecisionHandler = (id: string, decision: InboxDecision, comments: string) => Observable<any>;

/** getById responses wrap the record in `data`, sometimes as a one-element array. */
const unwrapRecord = (resp: any): any => {
  const data = resp?.data ?? resp;
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Approve / Reject straight from the notification Inbox, through each module's
 * existing approval API with the same payload its own approval screen sends.
 * Where that screen sends values from the request itself (travel advances, loan
 * EMIs), the request is fetched with the module's existing getById first and
 * those stored values are sent unchanged.
 *
 * The module API stays the authority: it re-checks that the caller is the
 * request's current approver and that the request is still pending.
 *
 * Modules with no approval screen / API in this app (dependent, passport,
 * letter, asset, amendment, tax declaration, restricted holiday, leave
 * cancellation) are not listed: there is no existing implementation to reuse.
 */
@Injectable({ providedIn: 'root' })
export class InboxApprovalActions {
  private readonly leaveRequests = inject(LeaveRequestService);
  private readonly leaveEncashment = inject(LeaveEncashService);
  private readonly attendanceRegularization = inject(AttendanceRegularizationService);
  private readonly documentRequests = inject(DocumentRequestService);
  private readonly travelRequests = inject(TravelRequestService);
  private readonly expenseRequests = inject(ExpenseRequestService);
  private readonly loanRequests = inject(LoanRequestService);
  private readonly loanAdjustments = inject(LoanAdjustmentService);
  private readonly leaveResumptions = inject(LeaveResumptionService);

  private readonly handlers: Record<string, DecisionHandler> = {
    LEAVE: (id, decision, comments) =>
      this.leaveRequests.updateStatus(id, { status: decision === 'approve' ? 'approved' : 'rejected', comments }),

    'LEAVE ENCASHMENT': (id, decision, comments) => {
      if (decision === 'approve') {
        const body = comments ? { comments } : {};
        return this.leaveEncashment.approveRequest(id, body);
      }
      return this.leaveEncashment.rejectRequest(id, { comments });
    },

    'LEAVE RESUMPTION': (id, decision, comments) =>
      this.leaveResumptions.approveOrRejectResumption(id, {
        status: decision === 'approve' ? 'Approved' : 'Rejected',
        comments,
      }),

    'ATTENDANCE REGULARIZATION': (id, decision, comments) =>
      this.attendanceRegularization.approveOrReject(id, {
        status: decision === 'approve' ? 'Approved' : 'Rejected',
        comments,
      }),

    DOCUMENT: (id, decision, comments) =>
      decision === 'approve'
        ? this.documentRequests.updateApproveBy(id, { status: 'Approved' })
        : this.documentRequests.updateApproveBy(id, { status: 'Rejected', approver_comments: comments }),

    // Same payload as the travel approval screen; the advances already on the request are kept.
    TRAVEL: (id, decision, comments) =>
      this.travelRequests.getById(id).pipe(
        map(unwrapRecord),
        switchMap((record) =>
          this.travelRequests.updateApproveBy(id, {
            status: decision === 'approve' ? 'Approved' : 'Rejected',
            workflow: record?.workflow,
            flight_advance: Number(record?.flight_advance || 0),
            stay_advance: Number(record?.stay_advance || 0),
            approver_comments: comments,
          }),
        ),
      ),

    // Travel settlement / expense: whole-claim decision, as on the expense approval list.
    'TRAVEL EXPENSE': (id, decision, comments) =>
      this.expenseRequests.getById(id).pipe(
        map(unwrapRecord),
        switchMap((record) =>
          this.expenseRequests.approveReject(id, {
            status: decision === 'approve' ? 'Approved' : 'Rejected',
            approver_comments: comments,
            workflow: record?.workflow,
          }),
        ),
      ),

    // Approves the loan as requested (amount / tenure amendments need the loan screen).
    'LOAN REQUEST': (id, decision, comments) =>
      this.loanRequests.updateStatus(id, decision === 'approve' ? 'Approved' : 'Rejected', comments),

    // Same payload builder the loan adjustment approval list uses, fed from the stored request.
    'LOAN ADJUSTMENT': (id, decision, comments) =>
      this.loanAdjustments.getById(id).pipe(
        map(unwrapRecord),
        switchMap((record) =>
          this.loanAdjustments.update(
            id,
            buildApprovalPayload({
              actionType: decision,
              comments,
              loanId: record?.loan?._id || record?.loan,
              emis: record?.emis || record?.emi_details,
            }),
          ),
        ),
      ),
  };

  supports(referenceModule: string | null | undefined): boolean {
    return !!referenceModule && referenceModule in this.handlers;
  }

  decide(referenceModule: string, id: string, decision: InboxDecision, comments: string): Observable<any> {
    return this.handlers[referenceModule](id, decision, comments);
  }
}
